/**
 * sharedChartWindow (#2955 §1) — the ONE timeline both Liquiditätsplanung
 * presentations span, plus the exact bridge between their two grains.
 *
 * **Why this module exists.** The curve and the columns used to be built over
 * different spans (a fixed ±12 months vs the ledger's *observed* first…last
 * month) and to hold SEPARATE window states, so switching presentation silently
 * changed the time range and threw the zoom/pan away. Both now span the same
 * fixed ±12 months, which is what makes a shared window possible at all: each
 * mode's rows array is COMPLETE over the span — the curve null-pads every day
 * (``fixedChartWindow``), the columns zero-fill every month (``monthlyColumns``)
 * — so index ↔ date is a total bijection in each grain and there is no
 * fractional window anywhere.
 *
 * **The canonical state is a DATE RANGE**, not an index window: an index means
 * nothing without the grain that produced it, and only a date survives the
 * translation. Each mode derives its own ``IndexWindow`` on the way in and
 * writes dates back on the way out.
 *
 * **Day → month → day is lossy, and the direction is a decision**: the month
 * grain snaps a range OUTWARD to whole months. Snapping inward can empty the
 * window (a range that lives inside one month); rounding to the nearest edge
 * drops data the farmer can currently see. So ``kurve → saeulen → kurve`` does
 * NOT return the exact day window — it returns a superset — and
 * ``__tests__/sharedChartWindow.test.ts`` pins that rather than leaving it to be
 * rediscovered as a bug.
 */
import { clampWindow, type IndexWindow } from '../../../components/charts/timeWindow';
import { addDays, addMonthsToYm, firstDayOfMonth, lastDayOfMonth } from './computeBalanceSeries';

/** Months of history the fixed window carries. */
export const CHART_WINDOW_MONTHS_BACK = 12;
/** Months of projection the fixed window carries. */
export const CHART_WINDOW_MONTHS_FORWARD = 12;
/** 25 — both edges inclusive, plus ``todayYm`` itself. */
export const CHART_WINDOW_MONTH_COUNT =
  CHART_WINDOW_MONTHS_BACK + CHART_WINDOW_MONTHS_FORWARD + 1;

/** An inclusive ``YYYY-MM-DD`` date range — the canonical shared-window state. */
export interface DateRange {
  startDate: string;
  endDate: string;
}

/** First month of the fixed window. */
export const windowFirstYm = (todayYm: string): string =>
  addMonthsToYm(todayYm, -CHART_WINDOW_MONTHS_BACK);

/** Last month of the fixed window. */
export const windowLastYm = (todayYm: string): string =>
  addMonthsToYm(todayYm, CHART_WINDOW_MONTHS_FORWARD);

/** The fixed window as dates: first day of the first month … last day of the last. */
export function chartWindowRange(todayYm: string): DateRange {
  return {
    startDate: firstDayOfMonth(windowFirstYm(todayYm)),
    endDate: lastDayOfMonth(windowLastYm(todayYm)),
  };
}

/** The window's 25 months, ascending — the column series' categories. */
export function chartWindowMonths(todayYm: string): string[] {
  const first = windowFirstYm(todayYm);
  return Array.from({ length: CHART_WINDOW_MONTH_COUNT }, (_, i) => addMonthsToYm(first, i));
}

const DAY_MS = 86_400_000;

/** Days since the epoch for a ``YYYY-MM-DD`` (UTC — the whole app's date basis). */
const dayNumber = (iso: string): number => Math.round(Date.parse(`${iso}T00:00:00Z`) / DAY_MS);

/** How many day rows the curve renders over the fixed window (leap-aware). */
export function chartWindowDayCount(todayYm: string): number {
  const { startDate, endDate } = chartWindowRange(todayYm);
  return dayNumber(endDate) - dayNumber(startDate) + 1;
}

/** Months from ``fromYm`` to ``toYm`` (signed). */
const monthsBetween = (fromYm: string, toYm: string): number => {
  const [fy, fm] = fromYm.split('-').map(Number);
  const [ty, tm] = toYm.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm);
};

/**
 * The curve's index window for a shared date range. Out-of-span dates clamp to
 * the span rather than producing negative indices.
 */
export function toDayWindow(range: DateRange, todayYm: string): IndexWindow {
  const origin = dayNumber(chartWindowRange(todayYm).startDate);
  return clampWindow(
    { start: dayNumber(range.startDate) - origin, end: dayNumber(range.endDate) - origin },
    chartWindowDayCount(todayYm),
  );
}

/** The shared date range a curve index window means. */
export function fromDayWindow(w: IndexWindow, todayYm: string): DateRange {
  const { startDate } = chartWindowRange(todayYm);
  const safe = clampWindow(w, chartWindowDayCount(todayYm));
  return { startDate: addDays(startDate, safe.start), endDate: addDays(startDate, safe.end) };
}

/**
 * The columns' index window for a shared date range, snapped OUTWARD to whole
 * months (see the module doc). ``clampWindow`` additionally applies the
 * ``MIN_VISIBLE_ROWS`` floor, which is what keeps a two-day curve window from
 * collapsing the column mode onto a single bar.
 */
export function toMonthWindow(range: DateRange, todayYm: string): IndexWindow {
  const first = windowFirstYm(todayYm);
  return clampWindow(
    {
      start: monthsBetween(first, range.startDate.slice(0, 7)),
      end: monthsBetween(first, range.endDate.slice(0, 7)),
    },
    CHART_WINDOW_MONTH_COUNT,
  );
}

/** The shared date range a column index window means — whole months, edge to edge. */
export function fromMonthWindow(w: IndexWindow, todayYm: string): DateRange {
  const first = windowFirstYm(todayYm);
  const safe = clampWindow(w, CHART_WINDOW_MONTH_COUNT);
  return {
    startDate: firstDayOfMonth(addMonthsToYm(first, safe.start)),
    endDate: lastDayOfMonth(addMonthsToYm(first, safe.end)),
  };
}
